import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import BreadCrumb from "Common/BreadCrumb";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import TableContainer from "Common/TableContainer";
import useCurrentRole from "Common/hooks/useCurrentRole";
import { canManageSalesAvailability } from "Common/constants/roles";

const BASE_URL = "https://portal-api.mahanpanel.com";
const getToken = () => localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");

interface SalesExpert {
  id: number;
  username: string;
  fullname: string;
  disabled: boolean;
  sales_enabled: boolean;
}

const extractList = (data: any): SalesExpert[] =>
  Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

const Index = () => {
  const { roleId } = useCurrentRole();
  const canManage = canManageSalesAvailability(roleId);
  const [users, setUsers] = useState<SalesExpert[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | "all" | null>(null);
  const [error, setError] = useState("");
  const masterRef = useRef<HTMLInputElement>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/sales-availability/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { sort_by: "fullname", sort_order: "asc" },
      });
      setUsers(extractList(response.data));
      setError("");
    } catch (e: any) {
      if (e.response?.status === 401) {
        window.location.assign("/login");
        return;
      }
      setError(e.response?.status === 403 ? "شما مجوز مشاهده این صفحه را ندارید" : "دریافت فهرست کارشناسان فروش ممکن نشد");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const enabledCount = users.filter((user) => user.sales_enabled).length;
  const allEnabled = users.length > 0 && enabledCount === users.length;
  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = enabledCount > 0 && enabledCount < users.length;
  }, [enabledCount, users.length]);

  const updateOne = async (user: SalesExpert, sales_enabled: boolean) => {
    if (busyId !== null || user.sales_enabled === sales_enabled) return;
    setBusyId(user.id);
    setUsers((current) => current.map((row) => row.id === user.id ? { ...row, sales_enabled } : row));
    try {
      const response = await axios.patch(`${BASE_URL}/sales-availability/${user.id}`, { sales_enabled }, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUsers((current) => current.map((row) => row.id === user.id ? response.data : row));
      setError("");
    } catch (e: any) {
      setUsers((current) => current.map((row) => row.id === user.id ? user : row));
      if (e.response?.status === 401) {
        window.location.assign("/login");
        return;
      }
      setError(e.response?.status === 403 ? "شما مجوز تغییر وضعیت فروش را ندارید" : "تغییر وضعیت فروش ذخیره نشد");
    } finally {
      setBusyId(null);
    }
  };

  const updateAll = async (sales_enabled: boolean) => {
    if (busyId !== null || users.length === 0) return;
    setBusyId("all");
    const previous = users;
    setUsers((current) => current.map((row) => ({ ...row, sales_enabled })));
    try {
      const response = await axios.put(`${BASE_URL}/sales-availability/`, { sales_enabled }, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUsers(extractList(response.data));
      setError("");
    } catch (e: any) {
      setUsers(previous);
      if (e.response?.status === 401) {
        window.location.assign("/login");
        return;
      }
      setError(e.response?.status === 403 ? "شما مجوز تغییر وضعیت فروش را ندارید" : "تغییر وضعیت همه کارشناسان ذخیره نشد");
    } finally {
      setBusyId(null);
    }
  };

  const columns = useMemo(() => [
    { header: "نام کارشناس فروش", accessorKey: "fullname", cell: (cell: any) => cell.getValue() || cell.row.original.username },
    { header: "نام کاربری", accessorKey: "username" },
    { header: "وضعیت حساب", accessorKey: "disabled", cell: (cell: any) => cell.getValue() ? "غیرفعال" : "فعال" },
    {
      header: "فعال بودن فروش",
      accessorKey: "sales_enabled",
      cell: (cell: any) => {
        const user: SalesExpert = cell.row.original;
        return <label className="inline-flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={user.sales_enabled} disabled={!canManage || busyId !== null} onChange={(event) => updateOne(user, event.target.checked)} />
          <span>{user.sales_enabled ? "فعال" : "غیرفعال"}</span>
        </label>;
      },
      enableSorting: false,
    },
  ], [canManage, busyId]);

  return <React.Fragment>
    <BreadCrumb pageTitle="فعال/غیرفعال کردن فروش" title="فعال/غیرفعال کردن فروش" />
    {error && <div className="mb-3 text-center text-red-500" role="alert">{error}</div>}
    <div className="card"><div className="card-body">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h6 className="grow text-15">کارشناسان فروش ({users.length})</h6>
        <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 font-medium">
          <input ref={masterRef} type="checkbox" checked={allEnabled} disabled={!canManage || busyId !== null || users.length === 0} onChange={(event) => updateAll(event.target.checked)} />
          <span>همه فعال</span>
        </label>
        <button type="button" className="btn border border-slate-300" onClick={fetchUsers} disabled={loading || busyId !== null} title="بارگذاری دوباره">
          {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        </button>
      </div>
      {loading ? <div className="flex h-32 items-center justify-center"><Loader2 className="size-8 animate-spin text-custom-500" /></div>
        : users.length ? <TableContainer isPagination columns={columns} data={users} customPageSize={20} divclassName="-mx-5 overflow-x-auto" tableclassName="w-full whitespace-nowrap" theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]" thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600" tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80" />
        : <LoadingCounter count={1} text="کارشناس فروشی برای نمایش وجود ندارد" />}
      {!canManage && <p className="mt-3 text-sm text-red-500">شما مجوز تغییر وضعیت فروش را ندارید</p>}
      <p className="mt-3 text-xs text-slate-500">غیرفعال کردن فروش فقط مانع ایجاد پیش‌فاکتور می‌شود و ورود کاربر را مسدود نمی‌کند.</p>
    </div></div>
  </React.Fragment>;
};

export default Index;
